// Loads the @techstark/opencv-js UMD build via a classic <script> tag whose
// src is the untransformed `?url` asset, waits for the emscripten runtime to
// finish initialising, and hands back the READY global `cv` object with its
// fake `then` stripped. The loader owns the whole lifecycle — script append,
// runtime wait, and failure cleanup — so a failed attempt never leaks a
// half-initialised runtime for the next attempt to get stuck on.
//
// Two traps conspire here, and both permanently locked the main thread on any
// trail containing a visionMatch page (renderer frozen: no paint, no input):
//
//  1. Emscripten's legacy MODULARIZE shim gives the Module object a fake
//     `.then` method. Resolving a Promise with that object — or returning it
//     from an async function / .then callback — makes the promise machinery
//     assimilate it as a thenable and call `.then` recursively, forever: an
//     unbounded MICROTASK loop the event loop can never escape
//     (emscripten/emscripten#5820). The strip below removes the property
//     before the object ever touches a Promise.
//
//  2. `import('@techstark/opencv-js')` cannot be made safe even with the
//     strip: the package's CJS exports surface `then` as a named export, so
//     the dynamic import's OWN promise assimilates the namespace before any
//     caller code runs. Hence the classic-script load — it also keeps the
//     ~10 MB file out of the bundler's transform pipeline entirely.
//
// The emitted asset keeps the source basename — dist/assets/opencv-<hash>.js
// — which is what vite.config.ts's precache globIgnores and sw.ts's runtime
// CacheFirst route match on.
import opencvUrl from '@techstark/opencv-js/dist/opencv.js?url';
// Type-only: erased at compile time, so this does NOT reintroduce the module
// import that trap 2 forbids.
import type * as CvNamespace from '@techstark/opencv-js';

type Cv = typeof CvNamespace;

// Embind classes like cv.Mat only exist once the wasm runtime has finished
// initialising; a runtime that never gets there (corrupt fetch, aborted
// compile) must reject rather than hang the caller forever.
const RUNTIME_INIT_TIMEOUT_MS = 30_000;

type CvGlobal = {
  Mat?: unknown;
  onRuntimeInitialized?: () => void;
};

const cvWindow = window as unknown as { cv?: CvGlobal };

/** See trap 1 above. Safe on every shape: only strips an own fake `then`. */
function stripFakeThenable(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    !(value instanceof Promise) &&
    typeof (value as { then?: unknown }).then === 'function'
  ) {
    delete (value as { then?: unknown }).then;
  }
  return value;
}

// `Mat` is the emscripten "runtime initialised" sentinel: embind registers
// every exported class in one pass when the wasm runtime comes up, so Mat's
// presence implies the rest of the API surface compareImages consumes.
const runtimeReady = (cv: CvGlobal | undefined): boolean =>
  !!cv && typeof cv.Mat === 'function';

/** Resolves once embind classes exist; bounded so a stuck init rejects. */
function waitForRuntime(cv: CvGlobal): Promise<CvGlobal> {
  if (runtimeReady(cv)) return Promise.resolve(cv);
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(
        new Error(
          `OpenCV wasm runtime did not initialize within ${RUNTIME_INIT_TIMEOUT_MS}ms`
        )
      );
    }, RUNTIME_INIT_TIMEOUT_MS);
    cv.onRuntimeInitialized = () => {
      window.clearTimeout(timeoutId);
      resolve(cv);
    };
  });
}

/**
 * Discard a dead runtime so the NEXT attempt evaluates a fresh script instead
 * of waiting on the same failed initialisation again. Assignment fallback
 * covers a non-configurable global (a top-level `var` declaration cannot be
 * deleted).
 */
function discardGlobal(): void {
  try {
    delete cvWindow.cv;
  } catch {
    // fall through to assignment
  }
  if (cvWindow.cv !== undefined) cvWindow.cv = undefined;
}

// Single-flight is the CALLER's job: compareImages.ts memoizes the returned
// promise, so this function never runs concurrently with itself.
export default async function loadOpenCvScript(): Promise<Cv> {
  // Reuse only a READY runtime (an earlier success, or a hot reload). A
  // present-but-uninitialised global is a failed prior attempt — discard it
  // and re-evaluate, otherwise every retry waits on the same dead runtime.
  if (runtimeReady(cvWindow.cv)) {
    return stripFakeThenable(cvWindow.cv) as Cv;
  }
  discardGlobal();

  const script = document.createElement('script');
  try {
    await new Promise<void>((resolve, reject) => {
      script.src = opencvUrl;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('OpenCV script failed to load'));
      document.head.appendChild(script);
    });
    const cv = cvWindow.cv;
    if (!cv) {
      throw new Error('OpenCV script loaded but did not define window.cv');
    }
    // Strip BEFORE the runtime wait: waitForRuntime resolves a Promise with
    // this object, which is exactly the assimilation trap 1 describes.
    stripFakeThenable(cv);
    return stripFakeThenable(await waitForRuntime(cv)) as Cv;
  } catch (error) {
    // Leave nothing behind: no dead script tag, no half-initialised global.
    script.remove();
    discardGlobal();
    throw error;
  }
}
